import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Container } from './Container';

/**
 * Keeps a contact's or matter's tabs at the top of the window while the page
 * scrolls, with its name once the profile header above has scrolled away
 * (OL-226). A zero-height marker sits just above the bar; once it leaves the
 * screen, the bar is stuck. From tablet width up only: on a phone the tabs
 * wrap to several rows and a stuck bar would cover the page.
 */
const StickyDetailBar = ({ title, children }) => {
  const marker = useRef(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!marker.current || typeof IntersectionObserver === 'undefined') return undefined;
    // The bar sticks under the app's own header once that header is fixed
    // (`.sticky-detail-bar` in style.css), so it counts as stuck that much sooner.
    const headerHeight = parseInt(getComputedStyle(marker.current).getPropertyValue('--tw-header-height'), 10) || 0;
    const observer = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), {
      rootMargin: `-${headerHeight}px 0px 0px 0px`,
    });
    observer.observe(marker.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={marker} aria-hidden="true" />
      <div
        className={`sticky-detail-bar md:sticky md:top-0 z-[9] bg-light dark:bg-coal-500 ${stuck ? 'md:shadow-sm' : ''}`}
        data-stuck={stuck ? 'true' : undefined}
      >
        {stuck && title && (
          <Container>
            <div className="hidden md:block pt-3 pb-2 text-base font-semibold text-gray-900 truncate" title={title}>
              {title}
            </div>
          </Container>
        )}
        {children}
      </div>
    </>
  );
};

StickyDetailBar.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

export { StickyDetailBar };
